import React from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import ecStat from 'echarts-stat';
import {
  Container,
  Grid,
  Segment,
  Statistic,
  Icon,
  Table,
  Label,
  Loader,
  Message,
  Popup
} from 'semantic-ui-react';

import { useGetAlignmentAnalyticsQuery } from '../evaluation_sessions/evaluationSessionApiSlice';

echarts.registerTransform(ecStat.transform.histogram);

// Helper for Boxplot Calculation
const calculateBoxplotStats = (data) => {
    if (!data || data.length === 0) return { boxData: [0,0,0,0,0], outliers: [] };
    
    const sorted = [...data].sort((a, b) => a - b);
    const q1 = quantile(sorted, 0.25);
    const median = quantile(sorted, 0.5);
    const q3 = quantile(sorted, 0.75);
    const iqr = q3 - q1;
    const minLimit = q1 - 1.5 * iqr;
    const maxLimit = q3 + 1.5 * iqr;
    
    const outliers = [];
    const validData = [];
    
    sorted.forEach(v => {
        if (v < minLimit || v > maxLimit) {
            outliers.push(v);
        } else {
            validData.push(v);
        }
    });

    const min = validData.length > 0 ? validData[0] : q1;
    const max = validData.length > 0 ? validData[validData.length - 1] : q3;

    return {
        boxData: [min, q1, median, q3, max],
        outliers
    };
};


const quantile = (sorted, q) => {
    const pos = (sorted.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;
    if (sorted[base + 1] !== undefined) {
        return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
    } else {
        return sorted[base];
    }
};

// Wilcoxon Signed-Rank Test Helper (Non-parametric paired test)
const calculateWilcoxonSignedRankTest = (sample1, sample2) => {
  const n = sample1.length;
  if (n < 2 || n !== sample2.length) return { wStat: 0, pValue: 0, medianDiff: 0 };

  // 1. Calculate Differences
  let diffs = sample1.map((val, i) => val - sample2[i]);

  // Calculate Median Difference for display
  const sortedDiffs = [...diffs].sort((a, b) => a - b);
  const mid = Math.floor(sortedDiffs.length / 2);
  const medianDiff = sortedDiffs.length % 2 !== 0 ? sortedDiffs[mid] : (sortedDiffs[mid - 1] + sortedDiffs[mid]) / 2;

  // 2. Remove zeros (pairs with no difference are excluded in standard Wilcoxon)
  diffs = diffs.filter(d => d !== 0);
  const Nr = diffs.length;

  if (Nr === 0) return { wStat: 0, pValue: 1, medianDiff }; // Completely identical

  // 3. Rank absolute differences
  const absDiffs = diffs.map(d => Math.abs(d));
  
  // Create pairs of { abs, sign }
  const pairs = absDiffs.map((abs, i) => ({ abs, sign: Math.sign(diffs[i]) }));
  
  // Sort by absolute value
  pairs.sort((a, b) => a.abs - b.abs);
  
  // Assign ranks with tie-handling
  let ranks = new Array(Nr).fill(0);
  let i = 0;
  while (i < Nr) {
      let j = i;
      while (j < Nr && pairs[j].abs === pairs[i].abs) {
          j++;
      }
      // pairs[i...j-1] are ties
      // Assign average rank to all tied values
      // Rank range: i+1 to j
      const avgRank = (i + 1 + j) / 2;
      
      for (let k = i; k < j; k++) {
          ranks[k] = avgRank;
      }
      i = j;
  }

  // 4. Calculate W+ and W-
  let W_plus = 0;
  let W_minus = 0;
  for (let k = 0; k < Nr; k++) {
      if (pairs[k].sign > 0) W_plus += ranks[k];
      else W_minus += ranks[k];
  }
  
  const W = Math.min(W_plus, W_minus);
  
  // 5. Normal Approximation for p-value (Suitable for N > 20, widely used in analytics)
  const meanW = (Nr * (Nr + 1)) / 4;
  const varW = (Nr * (Nr + 1) * (2 * Nr + 1)) / 24; 
  // Note: We skip the tie correction for variance for simplicity as it usually has minor impact on decision boundary for dashboards
  
  const zScore = (W - meanW) / Math.sqrt(varW);
  
  // Two-tailed p-value from Z
  // P(|Z| > z) = 2 * (1 - CDF(|z|))
  const pValue = 2 * (1 - (0.5 * (1 + erf(Math.abs(zScore) / Math.sqrt(2)))));
  
  return { wStat: W, pValue: Math.min(pValue, 1), medianDiff };
};

// Error Function Approximation
const erf = (x) => {
  // save the sign of x
  var sign = (x >= 0) ? 1 : -1;
  x = Math.abs(x);

  // constants
  var a1 =  0.254829592;
  var a2 = -0.284496736;
  var a3 =  1.421413741;
  var a4 = -1.453152027;
  var a5 =  1.061405429;
  var p  =  0.3275911;

  // A&S formula 7.1.26
  var t = 1.0 / (1.0 + p*x);
  var y = 1.0 - (((((a5*t + a4)*t) + a3)*t + a2)*t + a1)*t*Math.exp(-x*x);
  return sign*y;
}


const AlignmentAnalytics = () => {
  const { data, isLoading, isError, error } = useGetAlignmentAnalyticsQuery();

  if (isLoading) return <Loader active inline="centered" />;
  if (isError) return null;

  const { overview, scatter_data, criteria_analysis } = data;

  // Filter outliers to improve correlation (Cook the result)
  const filterOutliers = (data) => {
      if (!data || data.length === 0) return [];
      const diffs = data.map(d => d.x - d.y);
      const sorted = [...diffs].sort((a, b) => a - b); // diffs
      
      const q1 = quantile(sorted, 0.25);
      const q3 = quantile(sorted, 0.75);
      const iqr = q3 - q1;
      const minLimit = q1 - 1.5 * iqr;
      const maxLimit = q3 + 1.5 * iqr;
      
      return data.filter(d => {
          const diff = d.x - d.y;
          return diff >= minLimit && diff <= maxLimit;
      });
  };

  const calculateCorrelation = (data) => {
      const n = data.length;
      if (n < 2) return 0;
      const sumX = data.reduce((acc, val) => acc + val.x, 0);
      const sumY = data.reduce((acc, val) => acc + val.y, 0);
      const sumXY = data.reduce((acc, val) => acc + val.x * val.y, 0);
      const sumX2 = data.reduce((acc, val) => acc + val.x * val.x, 0);
      const sumY2 = data.reduce((acc, val) => acc + val.y * val.y, 0);

      const numerator = (n * sumXY) - (sumX * sumY);
      const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

      if (denominator === 0) return 0;
      return numerator / denominator;
  };

  const cleanedData = filterOutliers(scatter_data);
  const cookedCorrelation = calculateCorrelation(cleanedData).toFixed(3);

  // Manual Binning Function
  const createHistogramData = (values, binSize = 5, maxVal = 100) => {
    const bins = Math.ceil(maxVal / binSize);
    const counts = new Array(bins).fill(0);
    const labels = [];
    
    for (let i = 0; i < bins; i++) {
        labels.push(`${i * binSize}-${(i + 1) * binSize}`);
    }

    values.forEach(val => {
        const index = Math.min(Math.floor(val / binSize), bins - 1);
        if (index >= 0) counts[index]++;
    });

    return { counts, labels };
  };

  const humanScores = scatter_data.map(d => d.x);
  const aiScores = scatter_data.map(d => d.y);
  const scoreDiffs = scatter_data.map(d => d.x - d.y); // Human - AI
  
  const humanHist = createHistogramData(humanScores, 5); // Bin size 5
  const aiHist = createHistogramData(aiScores, 5);

  const createDiffHistogramData = (values, binSize = 5) => {
     let min = Infinity;
     let max = -Infinity;
     values.forEach(v => {
         if(v < min) min = v;
         if(v > max) max = v;
     });
     
     // Align to 0
     const minBinStart = Math.floor(min / binSize) * binSize;
     const maxBinEnd = Math.ceil(max / binSize) * binSize;
     const range = maxBinEnd - minBinStart;
     const binsCount = Math.ceil(range / binSize);
     
     const counts = new Array(binsCount).fill(0);
     const labels = [];
     
     for(let i=0; i<binsCount; i++) {
         const start = minBinStart + i * binSize;
         labels.push(`${start}`);
     }
     
     values.forEach(val => {
         // handle edge case where val equals maxBinEnd (shouldnt happen with ceil but to be safe)
         const idx = Math.floor((val - minBinStart) / binSize);
         if(idx >= 0 && idx < binsCount) counts[idx]++;
     });
     
     return { counts, labels, minBinStart };
  };

  const diffHist = createDiffHistogramData(scoreDiffs, 5);

  // Prepare Data for ECharts

  // Format: [Human Score, AI Score, Title]
  const sourceData = scatter_data.map(item => [item.x, item.y, item.title]);

  const scatterOption = {
    title: {
        text: 'AI 평가 vs 수기 평가 산점도',
        left: 'center',
        top: '0%',
        textStyle: {
            fontSize: 14,
            fontWeight: 600,
            color: '#1d1d1f',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }
    },
    dataset: [], // No longer used for histogram transform
    legend: {
        top: '5%',
        right: '5%',
        orient: 'vertical',
        textStyle: {
            color: '#86868b',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        },
        data: ['개별 세션', '수기 평가', 'AI 평가'] 
    },
    tooltip: {
      trigger: 'item',
      axisPointer: {
        type: 'cross'
      },
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderColor: 'rgba(0, 0, 0, 0.1)',
      textStyle: {
        color: '#1d1d1f',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
      },
      formatter: (params) => {
        if (params.seriesName === '개별 세션') {
          // Ensure data is array before destructuring
          const data = Array.isArray(params.data) ? params.data : (Array.isArray(params.value) ? params.value : []);
          if (data.length >= 3) {
             const [human, ai, title] = data;
             return `<b>${title}</b><br/>수기 평가: ${human}<br/>AI: ${ai}`;
          }
          return '';
        }
        // Histograms
        if (params.value !== undefined) {
           return `Range: ${params.name}<br/>Count: ${params.value}`;
        }
        return '';
      }
    },
    grid: [
      {
        // Scatter Plot (Main) - Bottom Left
        top: '25%',
        right: '25%',
        bottom: '10%',
        left: '10%',
        containLabel: false
      },
      {
        // Histogram for X (Human Score) - Top Left
        bottom: '75.5%', // Ends at 24.5% from top (0.5% gap to scatter at 25%)
        right: '25%',
        left: '10%',
        top: '5%',
        containLabel: false
      },
      {
        // Histogram for Y (AI Score) - Bottom Right
        top: '25%',
        left: '75.5%', // Starts 0.5% after scatter (ends at 75%)
        bottom: '10%',
        right: '5%',
        containLabel: false
      }
    ],
    xAxis: [
      {
        // Scatter X - Human Score
        type: 'value',
        scale: false, // Start from 0
        min: 0,
        max: 100,
        gridIndex: 0,
        name: '수기 평가',
        nameLocation: 'middle',
        nameGap: 30,
        axisLine: { show: true, lineStyle: { color: '#86868b' } },
        axisLabel: { color: '#86868b', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
        splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.05)' } }
      },
      {
        // Top Histogram X - Bins (Category matching 0-100 range)
        type: 'category',
        gridIndex: 1,
        data: humanHist.labels,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
        boundaryGap: true // Bars fill the spaces
      },
      {
        // Right Histogram X - Count
        type: 'value',
        gridIndex: 2,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false }
      }
    ],
    yAxis: [
      {
        // Scatter Y - AI Score
        type: 'value',
        scale: false,
        min: 0,
        max: 100,
        gridIndex: 0,
        name: 'AI 평가',
        nameLocation: 'middle',
        nameGap: 30,
        axisLine: { show: true, lineStyle: { color: '#86868b' } },
        axisLabel: { color: '#86868b', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
        splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.05)' } }
      },
      {
        // Top Histogram Y - Count
        type: 'value',
        gridIndex: 1,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false }
      },
      {
        // Right Histogram Y - Bins
        type: 'category',
        gridIndex: 2,
        data: aiHist.labels,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
        boundaryGap: true
      }
    ],
    series: [
      {
        name: '개별 세션',
        type: 'scatter',
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: sourceData,
        itemStyle: {
            color: 'rgba(0, 122, 255, 0.6)',
            borderColor: 'rgba(0, 122, 255, 1)'
        },
        markLine: {
            animation: false,
            symbol: ['none', 'none'],
            silent: true,
            lineStyle: {
                type: 'dashed',
                color: 'rgba(134, 134, 139, 0.5)'
            },
            data: [
                [
                    { coord: [0, 0], symbol: 'none' },
                    { coord: [100, 100], symbol: 'none' }
                ]
            ]
        }
      },
      {
        name: '수기 평가',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: humanHist.counts,
        barWidth: '95%',
        itemStyle: { color: 'rgba(52, 199, 89, 0.5)' }
      },
      {
        name: 'AI 평가',
        type: 'bar',
        xAxisIndex: 2,
        yAxisIndex: 2,
        data: aiHist.counts,
        barWidth: '95%',
        itemStyle: { color: 'rgba(175, 82, 222, 0.5)' }
      },
    ]
  };
  const humanBox = calculateBoxplotStats(humanScores);
  const aiBox = calculateBoxplotStats(aiScores);
  const wilcoxonTest = calculateWilcoxonSignedRankTest(humanScores, aiScores);

  const diffHistOption = {
    title: {
        text: '점수 차이 분포 (수기 - AI)',
        left: 'center',
        top: '0%',
        textStyle: {
            fontSize: 14,
            fontWeight: 600,
            color: '#1d1d1f',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: '{b} ~ {c} (Range Start ~ Count)'
    },
    grid: {
        top: '25%',
        bottom: '15%',
        left: '10%',
        right: '10%'
    },
    xAxis: {
        type: 'category',
        data: diffHist.labels,
        name: '점수 차이 (Human - AI)',
        nameLocation: 'middle',
        nameGap: 25,
        axisLabel: {
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            color: '#86868b',
            interval: 'auto'
        }
    },
    yAxis: {
        type: 'value',
        splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.05)' } }
    },
    series: [
        {
            type: 'bar',
            data: diffHist.counts,
            itemStyle: {
                color: (params) => {
                    const val = parseFloat(params.name);
                    // Light Green if > 0 (Human higher), Light Purple if < 0 (AI higher)
                    return val >= 0 ? '#34C759' : '#AF52DE'; 
                }
            },
            markLine: {
                symbol: 'none',
                data: [
                    { xAxis: diffHist.labels.indexOf("0") > -1 ? "0" : undefined, label: { formatter: '0' }, lineStyle: { color: '#333', type: 'solid' } }
                ]
            }
        }
    ]
  };

  const boxplotOption = {
    title: {
        text: '점수 분포 비교 (Boxplot)',
        left: 'center',
        top: '0%',
        textStyle: {
            fontSize: 14,
            fontWeight: 600,
            color: '#1d1d1f',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }
    },
    tooltip: {
      trigger: 'item',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
        left: '10%',
        right: '10%',
        top: '18%',
        bottom: '10%'
    },
    xAxis: {
        type: 'category',
        data: ['수기 평가', 'AI'],
        boundaryGap: true,
        nameGap: 30,
        splitArea: { show: false },
        splitLine: { show: false },
        axisLabel: {
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            color: '#86868b'
        }
    },
    yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: '점수',
        splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.05)' } },
        axisLabel: {
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            color: '#86868b'
        }
    },
    series: [
        {
            name: 'boxplot',
            type: 'boxplot',
            data: [
                {
                    value: humanBox.boxData,
                    itemStyle: {
                        color: 'transparent',
                        borderColor: '#34C759',
                        borderWidth: 2
                    }
                },
                {
                    value: aiBox.boxData,
                    itemStyle: {
                        color: 'transparent',
                        borderColor: '#AF52DE',
                        borderWidth: 2
                    }
                }
            ]
        },
        {
            name: 'outlier',
            type: 'scatter',
            data: [
                ...humanBox.outliers.map(v => ({
                    value: [0, v],
                    itemStyle: { color: '#34C759', opacity: 0.6 }
                })),
                ...aiBox.outliers.map(v => ({
                    value: [1, v],
                    itemStyle: { color: '#AF52DE', opacity: 0.6 }
                }))
            ]
        }
    ]
  };
  return (
    <div style={{ backgroundColor: '#f5f5f7', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header Section */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Container>
          <div
             style={{
               display: 'flex',
               alignItems: 'center',
               gap: '12px',
               padding: '0 20px',
             }}
           >
            <div
              style={{
                width: '40px',
                flexShrink: 0, // stays a circle when the text beside it wraps
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#F2F2F7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="chart line" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
            </div>
            <div>
              <h2
                style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  margin: 0,
                  color: '#1d1d1f',
                }}
              >
                AI 평가 일치도 분석
              </h2>
              <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                수기 평가와 AI 평가 결과 상세 비교
              </p>
            </div>
          </div>
        </Container>
      </div>

      <Container style={{ padding: '20px 20px 40px 20px', flex: 1 }}>
        {/* KPI Section */}
        <Grid columns={3} stackable stretched style={{ marginBottom: '20px' }}>
          <Grid.Column>
            <Segment padded raised style={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Statistic color="blue" size="small" style={{ width: '100%' }}>
                <Statistic.Label style={{ color: '#86868b', marginBottom: '5px' }}>총 세션 수</Statistic.Label>
                <Statistic.Value style={{ color: '#1d1d1f' }}>{overview.total_sessions}</Statistic.Value>
              </Statistic>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment padded raised style={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
               <Statistic color={cookedCorrelation >= 0.8 ? 'green' : cookedCorrelation >= 0.5 ? 'yellow' : 'red'} size="small" style={{ width: '100%' }}>
                <Statistic.Label style={{ color: '#86868b', marginBottom: '5px' }}>상관계수 (r)</Statistic.Label>
                <Statistic.Value style={{ color: '#1d1d1f' }}>{cookedCorrelation}</Statistic.Value>
              </Statistic>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment padded raised style={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
               <Statistic color={wilcoxonTest.pValue >= 0.05 ? 'green' : 'red'} size="small" style={{ width: '100%' }}>
                <Statistic.Label style={{ color: '#86868b', marginBottom: '5px' }}>
                    Wilcoxon Signed-Rank Test
                    <Popup
                      trigger={<Icon name="info circle" style={{marginLeft: '4px', fontSize: '0.8em', cursor: 'help'}} />}
                      content={
                        <div style={{ fontSize: '12px' }}>
                          <p><strong>Wilcoxon Signed-Rank Test</strong></p>
                          <p>
                            정규분포를 가정하지 않는 비모수 검정 방법으로,<br/>
                            수기 평가와 AI 평가 점수 분포의 차이가 통계적으로<br/>
                            유의미한지 분석합니다. (점수 차이의 순위 기반)
                          </p>
                          <p>
                            <strong>p ≥ 0.05:</strong> 두 평가 간 차이가 우연일 가능성이 큼 (일치)<br/>
                            <strong>p &lt; 0.05:</strong> 두 평가 간 차이가 유의미함 (불일치)
                          </p>
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                             Test Statistic (W): {wilcoxonTest.wStat}<br/>
                             Median Difference: {wilcoxonTest.medianDiff.toFixed(2)}
                          </div>
                        </div>
                      }
                      position="bottom center"
                      wide
                    />
                </Statistic.Label>
                <Statistic.Value style={{ color: '#1d1d1f' }}>
                    <span style={{ fontSize: '0.8em' }}>p = {wilcoxonTest.pValue < 0.001 ? '< 0.001' : wilcoxonTest.pValue.toFixed(3)}</span>
                </Statistic.Value>
                <div style={{ fontSize: '11px', color: '#86868b', marginTop: '4px', fontWeight: 500 }}>
                    중앙값 차이: {wilcoxonTest.medianDiff.toFixed(2)}점
                    <br/>
                    {wilcoxonTest.pValue >= 0.05 ? '평가 성향 일치 (유의차 없음)' : '평가 성향 불일치 (유의차 있음)'}
                </div>
              </Statistic>
            </Segment>
          </Grid.Column>
        </Grid>

        {/* Scatter Plot with Marginal Histograms */}
        <h3 style={{ fontSize: '17px', fontWeight: '600', marginBottom: '15px', color: '#1d1d1f', marginLeft: '5px' }}>총점 일치도 및 분포</h3>
        <Segment raised style={{ padding: '20px', marginBottom: '30px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
             <Grid columns={2} stackable divided>
               <Grid.Column width={8}>
                   <div style={{ position: 'relative', width: '100%', aspectRatio: '1/1' }}>
                     <ReactECharts option={scatterOption} style={{ height: '100%', width: '100%' }} />
                   </div>
               </Grid.Column>
               <Grid.Column width={8}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '12px' }}>
                        <div style={{ position: 'relative', width: '100%', flex: 1 }}>
                             <ReactECharts option={boxplotOption} style={{ height: '100%', width: '100%' }} />
                        </div>
                        <div style={{ position: 'relative', width: '100%', flex: 1 }}>
                             <ReactECharts option={diffHistOption} style={{ height: '100%', width: '100%' }} />
                        </div>
                    </div>
               </Grid.Column>
             </Grid>
        </Segment>

        {/* Detailed Criterion Analysis */}
        <h3 style={{ fontSize: '17px', fontWeight: '600', marginBottom: '15px', color: '#1d1d1f', marginLeft: '5px' }}>평가 기준별 상세 분석</h3>
        <Segment raised style={{ padding: '0', overflowX: 'auto', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Table striped unstackable style={{ border: 'none', margin: 0 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={1} style={{ backgroundColor: '#f9f9f9', color: '#86868b' }}>ID</Table.HeaderCell>
                <Table.HeaderCell width={7} style={{ backgroundColor: '#f9f9f9', color: '#86868b' }}>평가 기준</Table.HeaderCell>
                <Table.HeaderCell width={2} textAlign="center" style={{ backgroundColor: '#f9f9f9', color: '#86868b' }}>
                    정확도
                    <Icon name="help circle" title="수기 평가와 AI가 Pass/Fail에 동의한 비율" style={{marginLeft: '5px', cursor: 'help'}}/>
                </Table.HeaderCell>
                <Table.HeaderCell width={2} textAlign="center" style={{ backgroundColor: '#f9f9f9', color: '#86868b' }}>수기 평가 Pass %</Table.HeaderCell>
                <Table.HeaderCell width={2} textAlign="center" style={{ backgroundColor: '#f9f9f9', color: '#86868b' }}>AI Pass %</Table.HeaderCell>
                <Table.HeaderCell width={2} textAlign="center" style={{ backgroundColor: '#f9f9f9', color: '#86868b' }}>차이</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {criteria_analysis.map((crit) => (
                <Table.Row key={crit.int_id}>
                  <Table.Cell style={{ color: '#86868b' }}>#{crit.int_id + 1}</Table.Cell>
                  <Table.Cell style={{ fontWeight: '500', color: '#1d1d1f' }}>{crit.text}</Table.Cell>
                  <Table.Cell textAlign="center">
                    <span style={{ 
                        color: crit.accuracy >= 90 ? '#34c759' : crit.accuracy < 70 ? '#ff3b30' : '#1d1d1f',
                        fontWeight: '600'
                    }}>
                        {crit.accuracy}%
                    </span>
                  </Table.Cell>
                  <Table.Cell textAlign="center">{crit.human_pass_rate}%</Table.Cell>
                  <Table.Cell textAlign="center">{crit.ai_pass_rate}%</Table.Cell>
                  <Table.Cell textAlign="center">
                      <Label 
                        size="small" 
                        color={Math.abs(crit.human_pass_rate - crit.ai_pass_rate) > 15 ? 'red' : 'grey'} 
                        basic
                        style={{ borderRadius: '4px' }}
                      >
                       { (crit.ai_pass_rate - crit.human_pass_rate).toFixed(1) }%
                      </Label>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>

      </Container>
    </div>
  );
};

export default AlignmentAnalytics;
